import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, Disc, Loader2, Trash2, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import type { DashboardProject } from '../../project/types/project';
import { fetchPipeStatusByGroup } from '../api/pipeStatusOverview';
import { formatOverviewGroupLabel } from '../api/pipeStatusOverviewTypes';
import { mapPipeStatusToTableRows } from '../helpers/mapPipeStatusTableRows';
import { countPipeEndPresence } from '../helpers/usePipeStatusFilters';
import { PIPEFITTER_FULLSCREEN_DIALOG_CLASS } from '../constants/uiConstants';
import { UpdatePipeFlagsDialog, type UpdatePipeFlagsDialogPipe } from './UpdatePipeFlagsDialog';
import { deletePipeEnds, type PipeEndKey } from '../../../services/pipeIdEditApi';
import { Notification as customNotification } from '../../../components/ui/custom-notification';
import {
  Dialog as ConfirmDialog,
  DialogContent as ConfirmDialogContent,
  DialogFooter,
  DialogTitle as ConfirmDialogTitle,
} from '../../../components/ui/dialog';

interface PipeGroupPipesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: DashboardProject;
  /** Optional label for dialog title. */
  titleHint?: string;
  /** Material pipe-size ids to scope (empty = project-wide within groups). */
  pipeIds?: Array<string | number>;
  groupIds: string[];
  canEdit?: boolean;
  onUpdated?: () => void;
}

export const PipeGroupPipesDialog: React.FC<PipeGroupPipesDialogProps> = ({
  open,
  onOpenChange,
  project,
  titleHint,
  pipeIds = [],
  groupIds,
  canEdit = true,
  onUpdated,
}) => {
  const projectNumber = project?.project?.projectNumber ?? '';
  const projectId = project?.project?.projectId;
  const projectName = project?.project?.projectName ?? '';

  const [rows, setRows] = useState<ReturnType<typeof mapPipeStatusToTableRows>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<(string | number)[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [archivePipes, setArchivePipes] = useState<UpdatePipeFlagsDialogPipe[]>([]);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [flangePipes, setFlangePipes] = useState<UpdatePipeFlagsDialogPipe[]>([]);
  const [flangeOpen, setFlangeOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEndA, setDeleteEndA] = useState(true);
  const [deleteEndB, setDeleteEndB] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!open || groupIds.length === 0 || (!projectNumber && projectId == null)) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const items = await fetchPipeStatusByGroup({
        projectId,
        projectNumber,
        projectName,
        includeArchived,
        groupIds,
        pipeIds: pipeIds.length > 0 ? pipeIds : undefined,
      });
      setRows(mapPipeStatusToTableRows(items as unknown as Record<string, unknown>[]));
      setSelectedKeys([]);
    } catch (err: any) {
      setError(err?.message || 'Failed to load group pipes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [open, groupIds, pipeIds, projectId, projectNumber, projectName, includeArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const endPresenceCounts = useMemo(() => countPipeEndPresence(rows), [rows]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedKeys.some((k) => String(k) === String(r.id))),
    [rows, selectedKeys]
  );

  const isAllSelected =
    rows.length > 0 && rows.every((r) => selectedKeys.some((k) => String(k) === String(r.id)));
  const isSomeSelected =
    rows.some((r) => selectedKeys.some((k) => String(k) === String(r.id))) && !isAllSelected;

  const titleGroups = groupIds.map(formatOverviewGroupLabel).join(', ');

  const toFlagPipe = (r: any): UpdatePipeFlagsDialogPipe => ({
    id: r.id,
    pipeID: r.pipeID,
    isArchive: r.isArchive,
    isFlange: r.isFlange,
    isAnode: r.isAnode,
  });

  const handleDeleteOk = async () => {
    const ends: PipeEndKey[] = [];
    if (deleteEndA) ends.push('EndA');
    if (deleteEndB) ends.push('EndB');
    if (ends.length === 0 || !projectNumber || selectedRows.length === 0) return;
    setIsDeleting(true);
    try {
      for (const pipe of selectedRows) {
        const statusId = Number(pipe.id);
        if (!Number.isFinite(statusId) || statusId <= 0) continue;
        await deletePipeEnds({
          id: statusId,
          projectNumber: String(projectNumber),
          ends,
        });
      }
      customNotification.success('Ends deleted');
      setDeleteOpen(false);
      setSelectedKeys([]);
      await load();
      onUpdated?.();
    } catch (err: any) {
      customNotification.error(
        'Delete failed',
        err?.response?.data?.message || err?.message || 'Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={`${PIPEFITTER_FULLSCREEN_DIALOG_CLASS} flex flex-col`}>
          <DialogHeader className="flex-shrink-0 border-b border-[#E2E5EA] px-4 py-3">
            <DialogTitle className="text-[16px] font-semibold text-blueprint-blue">
              {titleHint ? `${titleHint} · ` : ''}
              {titleGroups}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-auto p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-heritage-grey">
              <span>
                Showing {rows.length} pipe{rows.length !== 1 ? 's' : ''}
                {' — '}
                Both: <span className="font-semibold text-text-grey">{endPresenceCounts.both}</span>
                {' — '}
                Only End A:{' '}
                <span className="font-semibold text-text-grey">{endPresenceCounts.endAOnly}</span>
                {' — '}
                Only End B:{' '}
                <span className="font-semibold text-text-grey">{endPresenceCounts.endBOnly}</span>
              </span>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none text-text-grey">
                <Checkbox
                  checked={includeArchived}
                  onCheckedChange={(v) => setIncludeArchived(v === true)}
                />
                Include archived pipes
              </label>
            </div>

            {selectedKeys.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                <span className="text-[12px] text-text-grey">
                  {selectedKeys.length} selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-[12px]"
                  onClick={() => setSelectedKeys([])}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Clear
                </Button>
                {canEdit && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-[12px] text-readable-red border-readable-red"
                      onClick={() => {
                        setDeleteEndA(selectedRows.some((p) => p.endA));
                        setDeleteEndB(selectedRows.some((p) => p.endB));
                        setDeleteOpen(true);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />
                      Delete
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-[12px]"
                      onClick={() => {
                        setArchivePipes(selectedRows.map(toFlagPipe));
                        setArchiveOpen(true);
                      }}
                    >
                      <Archive className="h-3.5 w-3.5 mr-1" />
                      Mark as Archived
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-[12px]"
                      onClick={() => {
                        setFlangePipes(selectedRows.map(toFlagPipe));
                        setFlangeOpen(true);
                      }}
                    >
                      <Disc className="h-3.5 w-3.5 mr-1" />
                      Mark as Flange
                    </Button>
                  </>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center gap-2 py-16 text-heritage-grey">
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading…
              </div>
            ) : error ? (
              <div className="text-center py-12 space-y-3">
                <p className="text-sm text-red-600">{error}</p>
                <Button size="sm" variant="outline" onClick={() => void load()}>
                  Retry
                </Button>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-12 text-sm text-heritage-grey">
                No pipes in this group.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        ref={(el) => {
                          if (el) (el as any).indeterminate = isSomeSelected;
                        }}
                        onCheckedChange={(checked) => {
                          setSelectedKeys(checked === true ? rows.map((r) => r.id) : []);
                        }}
                      />
                    </TableHead>
                    <TableHead>SL NO</TableHead>
                    <TableHead>PIPE ID</TableHead>
                    <TableHead>GROUP</TableHead>
                    <TableHead>END A</TableHead>
                    <TableHead>END B</TableHead>
                    <TableHead>STATUS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, idx) => {
                    const checked = selectedKeys.some((k) => String(k) === String(row.id));
                    return (
                      <TableRow key={String(row.id)}>
                        <TableCell>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              setSelectedKeys((prev) => {
                                if (v === true) return [...prev, row.id];
                                return prev.filter((k) => String(k) !== String(row.id));
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell className="font-medium">{row.pipeID}</TableCell>
                        <TableCell>
                          {formatOverviewGroupLabel(
                            String((row as any).groupId ?? (row as any).groupID ?? '')
                          )}
                        </TableCell>
                        <TableCell>{row.endA ? 'Yes' : '—'}</TableCell>
                        <TableCell>{row.endB ? 'Yes' : '—'}</TableCell>
                        <TableCell>{row.status}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <UpdatePipeFlagsDialog
        open={archiveOpen}
        onOpenChange={(next) => {
          setArchiveOpen(next);
          if (!next) setArchivePipes([]);
        }}
        pipes={archivePipes}
        mode="archive"
        onUpdated={() => {
          void load();
          onUpdated?.();
          setSelectedKeys([]);
        }}
      />

      <UpdatePipeFlagsDialog
        open={flangeOpen}
        onOpenChange={(next) => {
          setFlangeOpen(next);
          if (!next) setFlangePipes([]);
        }}
        pipes={flangePipes}
        mode="flange"
        onUpdated={() => {
          void load();
          onUpdated?.();
          setSelectedKeys([]);
        }}
      />

      <ConfirmDialog open={deleteOpen} onOpenChange={(o) => !isDeleting && setDeleteOpen(o)}>
        <ConfirmDialogContent className="max-w-md">
          <ConfirmDialogTitle>Delete ends</ConfirmDialogTitle>
          <p className="text-sm text-heritage-grey mb-3">
            Delete selected ends for {selectedRows.length} pipe
            {selectedRows.length !== 1 ? 's' : ''}.
          </p>
          <div className="flex gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={deleteEndA} onCheckedChange={(v) => setDeleteEndA(v === true)} />
              End A
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={deleteEndB} onCheckedChange={(v) => setDeleteEndB(v === true)} />
              End B
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isDeleting} onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 text-white"
              disabled={isDeleting || (!deleteEndA && !deleteEndB)}
              onClick={() => void handleDeleteOk()}
            >
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </ConfirmDialogContent>
      </ConfirmDialog>
    </>
  );
};
